import type {
  CourseSnapshot,
  RoundParticipantInput,
} from "../../../../domain/round/round-types.js";
import type { CreateLocalRoundInput } from "../../../sync/round-sync-engine.js";
import {
  validateReview,
  type ReviewCard,
  type ReviewState,
} from "./ggb-review.js";
import { importPlayedAt } from "./ggb-review-validation.js";

export interface ImportParticipant {
  id: string;
  displayName: string;
  scores: Record<number, number>;
}

export interface ReviewedImportInput {
  roundId: string;
  setup: Omit<CreateLocalRoundInput, "roundId">;
  participants: ImportParticipant[];
}

/** No inferred format, handicap, date, par or stroke-index enters the round. */
export function buildImportSetup(
  review: ReviewState,
  resolved: Map<string, { id: string; displayName: string }>,
  roundId: string,
): ReviewedImportInput {
  const problem = validateReview(review, undefined);
  if (problem) throw new Error(problem);
  if (review.detail === "totals-only")
    throw new Error("Totals-only results use the atomic server import flow.");
  if (review.datePrecision && review.datePrecision !== "instant")
    throw new Error("Date-only and unknown dates use the server import flow.");
  if (!review.format || !review.scoringBasis)
    throw new Error("Choose the scoring agreement.");
  const cards = review.cards.filter((card) => card.assign !== "");
  if (
    cards.some((card) => review.holes.some((hole) => card.scores[hole] === "P"))
  )
    throw new Error("Reviewed pickups use the server import flow.");
  const participants = cards.map((card) => {
    const target = resolved.get(card.key);
    if (!target)
      throw new Error(`${card.name}: the player assignment is not resolved.`);
    return {
      ...target,
      scores: Object.fromEntries(
        review.holes.map((hole) => [hole, Number(card.scores[hole])]),
      ),
    };
  });
  if (new Set(participants.map((p) => p.id)).size !== participants.length)
    throw new Error("Each player can receive only one scorecard.");
  const first = cards[0]!;
  const course: CourseSnapshot = {
    id: `gamebook:${roundId}`,
    source: "manual",
    name: review.course.trim(),
    teeName: first.teeName.trim(),
    rating: null,
    slope: null,
    holes: cardHoles(first, review.holes),
  };
  const setup: ReviewedImportInput["setup"] = {
    schemaVersion: 3,
    scorekeeperGeneration: 0,
    orderedHoleNumbers: [...review.holes],
    course,
    participants: cards.map((card, index) =>
      participantInput(card, participants[index]!, course, review.holes),
    ),
    conditions: { playedAt: importPlayedAt(review), cart: null, weather: null },
    rules: {
      kind: "ggb-import",
      format: review.format,
      handicapMode:
        review.scoringBasis === "gross"
          ? "off"
          : review.format === "match-play"
            ? "relative"
            : "full",
      metric: null,
    },
    mainContest: {
      id: `${roundId}:main`,
      format: review.format,
      scoringBasis: review.scoringBasis,
      sides: participants.map((p) => ({
        id: p.id,
        kind: "person",
        participantIds: [p.id],
      })),
      holeNumbers: [...review.holes],
      scope: "round",
      rulesVersion: 1,
      lockedAt: null,
    },
  };
  return { roundId, setup, participants };
}

function cardHoles(card: ReviewCard, holes: number[]) {
  return holes.map((number) => ({
    number,
    par: Number(card.par[number]),
    strokeIndex: Number(card.si[number]),
  }));
}

function participantInput(
  card: ReviewCard,
  target: ImportParticipant,
  course: CourseSnapshot,
  holes: number[],
): RoundParticipantInput {
  return {
    id: target.id,
    displayName: target.displayName,
    handicapIndex: null,
    teamId: null,
    teeSnapshot: {
      courseId: course.id,
      teeId: null,
      teeName: card.teeName.trim(),
      rating: null,
      slope: null,
      source: "manual",
      holes: cardHoles(card, holes),
    },
    handicapSnapshot: {
      playingHandicap: card.playingHandicap.trim()
        ? Number(card.playingHandicap)
        : null,
      appliesToHoleNumbers: [...holes],
      source: "agreed",
      agreedAt: null,
    },
  };
}
